import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError, checkSubscriptionActive } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";
import AddRoles from "./AddRoles";
import UpdateRoles from "./UpdateRoles";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Tooltip,
  TextField,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  deleteRole,
  fetchRolesWithUsage,
} from "../../../redux/Slice/Settings/settingsSlice";
import { fetchUserPermissions } from "../../../redux/Slice/User/userDetailsSlice";
import AuthView from "../../../utils/AuthView";

const RolesPermission = () => {
  const dispatch = useDispatch();
 // 1536px and above

  const [showAddRole, setShowAddRole] = useState(false);
  const [showUpdateRole, setShowUpdateRole] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [rolesPerPage, setRolesPerPage] = useState(10); // Default to 10 for all screens
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const { rolesWithUsage = [], loading } = useSelector(
    (state) => state.settings
  );

  const RolePermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Role || {}
  );

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => prev + direction);
  };

  const handleRowsPerPageChange = (event) => {
    setRolesPerPage(event.target.value);
    setCurrentPage(1);
  };

  const indexOfLastRole = currentPage * rolesPerPage;
  const indexOfFirstRole = indexOfLastRole - rolesPerPage;
  const filteredRoles = Array.isArray(rolesWithUsage)
    ? rolesWithUsage.filter((role) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          role.roleName.toLowerCase().includes(searchLower) ||
          (role.description && role.description.toLowerCase().includes(searchLower))
        );
      })
    : [];
  const currentRoles = filteredRoles.slice(indexOfFirstRole, indexOfLastRole);

  const handleAddRoleClick = async () => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    setShowAddRole(true);
  };

  const handleBackFromAddRole = () => {
    setShowAddRole(false);
  };

  const handleDeleteRole = async (_id) => {
    if (!_id) return;
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    dispatch(deleteRole(_id))
      .unwrap()
      .then(() => {
        dispatch(fetchRolesWithUsage());
        toast.success("Role deleted successfully.");
      })
      .catch((error) => {
        if (!isSubscriptionBlockedError(error)) {
          toast.error(typeof error === 'string' ? error : "Failed to delete role.");
        }
      });
  };

  const handleRoleNameClick = async (roleId) => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    setSelectedRoleId(roleId);
    setShowUpdateRole(true);
  };

  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(fetchRolesWithUsage());
  }, [dispatch]);

  const addEditRoleShellSx = {
    flex: 1,
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  if (showAddRole) {
    return (
      <Box sx={addEditRoleShellSx}>
        <AddRoles onBack={handleBackFromAddRole} />
      </Box>
    );
  }

  if (showUpdateRole && selectedRoleId) {
    return (
      <Box sx={addEditRoleShellSx}>
        <UpdateRoles
          roleId={selectedRoleId}
          onBack={() => setShowUpdateRole(false)}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: { xs: "12px", sm: "16px", md: "20px", lg: "20px", xl: "20px" },
        height: "100%",
        minHeight: 0,
        flex: 1,
        borderRadius: "8px",
        backgroundColor: "background.paper",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        display: "flex",
        flexDirection: "column",
        color: "text.primary",
        position: "relative",
        overflow: "hidden", // Prevent main container from scrolling
      }}
    >

      {!RolePermissions.View ? (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            paddingLeft: "2rem",
            paddingTop: "2rem",
            zIndex: 1,
      }}
    >
          <AuthView />
        </Box>
      ) : (
        <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: { xs: "0.75rem", sm: "1rem", md: "1.5rem", lg: "2rem", xl: "2rem" },
          gap: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search roles..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          size="small"
          sx={{
            flex: 1,
            fontSize: { xs: "0.875rem", sm: "0.9rem", md: "1rem", lg: "1rem", xl: "1rem" },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
              fontSize: { xs: "0.875rem", sm: "0.9rem", md: "1rem", lg: "1rem", xl: "1rem" },
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
              fontSize: { xs: "0.875rem", sm: "0.9rem", md: "1rem", lg: "1rem", xl: "1rem" },
              padding: { xs: "8px 12px", sm: "10px 14px", md: "14px", lg: "14px", xl: "14px" },
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.23)" 
                  : "rgba(0, 0, 0, 0.23)",
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.5)" 
                  : "rgba(0, 0, 0, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
              },
            },
          }}
        />
        <Tooltip
          title={!RolePermissions.Add ? "You don't have permission to add roles" : ""}
          arrow
        >
          <span>
            <Button
              variant="contained"
              endIcon={<AddCircleOutlineIcon sx={{ fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem", lg: "1.25rem", xl: "1.25rem" } }} />}
              onClick={handleAddRoleClick}
              disabled={!RolePermissions.Add}
              size="small"
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "20px",
                fontWeight: "bold",
                textTransform: "capitalize",
                paddingX: { xs: "0.75rem", sm: "0.875rem", md: "1rem", lg: "1rem", xl: "1rem" },
                paddingY: { xs: "0.3rem", sm: "0.35rem", md: "0.4rem", lg: "0.4rem", xl: "0.4rem" },
                fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                marginLeft: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
                "&.Mui-disabled": {
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                  color: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.38)",
                },
              }}
            >
              Add Role
            </Button>
          </span>
        </Tooltip>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "1rem",
          flex: 1,
          minHeight: 0,
          maxHeight: {
            xs: "none",
            sm: "none",
            md: "calc(100vh - 260px)",
            lg: "calc(100vh - 280px)",
            xl: "calc(100vh - 280px)",
          },
          overflowY: "auto",
          backgroundColor: "background.paper",
          // Decrease scrollbar size for all screen sizes
          scrollbarWidth: { xs: "thin", sm: "thin", md: "thin", lg: "thin", xl: "thin" }, // Firefox
          "&::-webkit-scrollbar": {
            width: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Vertical scrollbar - very thin for md/lg/xl
            height: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Horizontal scrollbar - very thin for md/lg/xl
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#555" : "#888",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#777" : "#555",
            },
          },
          border: (theme) => theme.palette.mode === "dark" 
            ? "1px solid rgba(255, 255, 255, 0.12)" 
            : "1px solid rgba(0, 0, 0, 0.12)",
          "& .MuiTableCell-root": {
            borderBottom: (theme) => theme.palette.mode === "dark" 
              ? "1px solid rgba(255, 255, 255, 0.12)" 
              : "1px solid rgba(0, 0, 0, 0.12)",
            color: "text.primary",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "background.paper",
            color: "text.primary",
            fontWeight: "bold",
          },
          "& .MuiTableRow-root:hover": {
            backgroundColor: (theme) => theme.palette.mode === "dark" 
              ? "rgba(255, 255, 255, 0.08)" 
              : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Description
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Used By
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
                  ? Array.from({ length: rolesPerPage }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      <Skeleton
                        variant="text"
                        width="80%"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      <Skeleton
                        variant="text"
                        width="60%"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      <Skeleton
                        variant="text"
                        width="40%"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={40}
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                      />
                    </TableCell>
                  </TableRow>
                ))
                  : currentRoles.map((role) => {
                  const isDeleteDisabled =
                        role.roleName === "Owner" || 
                        role.usedBy !== 0 || 
                        !RolePermissions.Delete;
                  const disableReason =
                    role.roleName === "Owner"
                      ? "Cannot delete Owner role"
                          : role.usedBy !== 0
                          ? "Cannot delete, role is in use"
                          : "You don't have permission to delete roles";

                  return (
                    <TableRow
                      key={role._id}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <TableCell
                            onClick={() => RolePermissions.Edit && handleRoleNameClick(role._id)}
                        sx={{
                              cursor: RolePermissions.Edit ? "pointer" : "default",
                              color: RolePermissions.Edit ? "primary.main" : "text.primary",
                              textDecoration: "none",
                              padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                          "&:hover": {
                                textDecoration: RolePermissions.Edit ? "underline" : "none",
                          },
                        }}
                      >
                            <Tooltip
                              title={!RolePermissions.Edit ? "You don't have permission to edit roles" : ""}
                              arrow
                            >
                              <span>{role.roleName}</span>
                            </Tooltip>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          color: "text.secondary",
                          padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                        }}
                      >
                        {role.description}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                        }}
                      >
                        {role.usedBy}
                      </TableCell>
                      <TableCell sx={{ padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                        {isDeleteDisabled ? (
                          <Tooltip title={disableReason} arrow>
                            <span>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={(e) => {
                                      e.stopPropagation();
                                  handleDeleteRole(role._id);
                                }}
                                disabled={isDeleteDisabled}
                                sx={{
                                  borderColor: "error.main",
                                  color: "error.main",
                                  borderRadius: "20px",
                                  textTransform: "capitalize",
                                  fontWeight: "bold",
                                  padding: { xs: "4px 8px", sm: "6px 10px", md: "8px 12px", lg: "8px 12px", xl: "8px 12px" },
                                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem", lg: "0.8rem", xl: "0.8rem" },
                                  minWidth: { xs: "50px", sm: "60px", md: "70px", lg: "70px", xl: "70px" },
                                  "&:hover": {
                                    backgroundColor: "error.main",
                                    color: "error.contrastText",
                                    borderColor: "error.main",
                                  },
                                  "&.Mui-disabled": {
                                    color: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.38)",
                                    borderColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                                  },
                                }}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={(e) => {
                                  e.stopPropagation();
                              handleDeleteRole(role._id);
                            }}
                            sx={{
                              borderColor: "error.main",
                              color: "error.main",
                              borderRadius: "20px",
                              textTransform: "capitalize",
                              fontWeight: "bold",
                              padding: { xs: "4px 8px", sm: "6px 10px", md: "8px 12px", lg: "8px 12px", xl: "8px 12px" },
                              fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem", lg: "0.8rem", xl: "0.8rem" },
                              minWidth: { xs: "50px", sm: "60px", md: "70px", lg: "70px", xl: "70px" },
                              "&:hover": {
                                backgroundColor: "error.main",
                                color: "error.contrastText",
                                borderColor: "error.main",
                              },
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: { xs: "0.25rem", sm: "0.5rem", md: "0.5rem", lg: "0.75rem", xl: "0.75rem" },
          marginBottom: { xs: "0px", sm: "0px", md: "0px", lg: "0px", xl: "0px" },
          paddingTop: { xs: "0.5rem", sm: "0", md: "0", lg: "0", xl: "0" },
          paddingBottom: { xs: "0px", sm: "0", md: "0", lg: "0", xl: "0" },
          flexShrink: 0,
          gap: { xs: 0.25, sm: 1, md: 1, lg: 1, xl: 1 },
        }}
      >
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" },
          }}
        >
          {indexOfFirstRole + 1}-
          {Math.min(indexOfLastRole, rolesWithUsage.length)} of{" "}
          {rolesWithUsage.length}
        </Typography>

        <FormControl size="small">
          <InputLabel 
            sx={{ 
              color: "text.secondary",
              fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" },
            }}
          >
            Rows per page
          </InputLabel>
          <Select
            value={rolesPerPage}
            onChange={handleRowsPerPageChange}
            label="Rows per page"
            sx={{
              color: "text.primary",
              fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" },
              minHeight: { xs: "20px", sm: "28px", md: "30px", lg: "36px", xl: "36px" },
              padding: { xs: "0px 4px", sm: "2px 8px", md: "3px 10px", lg: "4px 12px", xl: "4px 12px" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.23)" 
                  : "rgba(0, 0, 0, 0.23)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.5)" 
                  : "rgba(0, 0, 0, 0.5)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
              "& .MuiSelect-icon": {
                color: "text.secondary",
                fontSize: { xs: "0.75rem", sm: "1rem", md: "1.1rem", lg: "1.25rem", xl: "1.25rem" },
              },
            }}
          >
            <MenuItem value={5} sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}>5</MenuItem>
            <MenuItem value={10} sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}>10</MenuItem>
            <MenuItem value={20} sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}>20</MenuItem>
          </Select>
        </FormControl>

        <Box display="flex" sx={{ gap: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 } }}>
          <IconButton
            onClick={() => handlePageChange(-1)}
            disabled={currentPage === 1}
            size="small"
            sx={{
              color: (theme) => theme.palette.mode === "dark" 
                ? "text.secondary" 
                : "rgba(0, 0, 0, 0.6)",
              padding: { xs: "0px", sm: "2px", md: "3px", lg: "4px", xl: "4px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "0.75rem", sm: "1rem", md: "1.1rem", lg: "1.25rem", xl: "1.25rem" },
              },
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.08)" 
                  : "rgba(0, 0, 0, 0.04)",
                color: (theme) => theme.palette.mode === "dark" 
                  ? "text.secondary" 
                  : "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-disabled": {
                color: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.3)" 
                  : "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(1)}
            disabled={indexOfLastRole >= rolesWithUsage.length}
            size="small"
            sx={{
              color: (theme) => theme.palette.mode === "dark" 
                ? "text.secondary" 
                : "rgba(0, 0, 0, 0.6)",
              padding: { xs: "0px", sm: "2px", md: "3px", lg: "4px", xl: "4px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "0.75rem", sm: "1rem", md: "1.1rem", lg: "1.25rem", xl: "1.25rem" },
              },
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.08)" 
                  : "rgba(0, 0, 0, 0.04)",
                color: (theme) => theme.palette.mode === "dark" 
                  ? "text.secondary" 
                  : "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-disabled": {
                color: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.3)" 
                  : "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>
        </>
      )}
    </Box>
  );
};

export default RolesPermission;
